import dayjs, { Dayjs } from "dayjs";
import {
  IAccountManagementCustomerListFilter,
  IAccountManagementProfitLossFilter,
} from "../../types/AccountManagementCustomer";

export type QuickDatePreset = "today" | "yesterday" | "thisWeek" | "thisMonth" | "lastMonth";

export const QUICK_DATE_PRESETS: { key: QuickDatePreset; label: string }[] = [
  { key: "today", label: "Bugün" },
  { key: "yesterday", label: "Dün" },
  { key: "thisWeek", label: "Bu Hafta" },
  { key: "thisMonth", label: "Bu Ay" },
  { key: "lastMonth", label: "Geçen Ay" },
];

export const currentMonthRange = (): { from: string; to: string } => {
  const now = dayjs();
  return {
    from: now.startOf("month").format("YYYY-MM-DD"),
    to: now.endOf("month").format("YYYY-MM-DD"),
  };
};

export const presetToRange = (preset: QuickDatePreset): [Dayjs, Dayjs] => {
  const now = dayjs();
  switch (preset) {
    case "today":
      return [now.startOf("day"), now.endOf("day")];
    case "yesterday": {
      const y = now.subtract(1, "day");
      return [y.startOf("day"), y.endOf("day")];
    }
    case "thisWeek":
      return [now.startOf("week"), now.endOf("week")];
    case "thisMonth":
      return [now.startOf("month"), now.endOf("month")];
    case "lastMonth": {
      const m = now.subtract(1, "month");
      return [m.startOf("month"), m.endOf("month")];
    }
    default:
      return [now.startOf("month"), now.endOf("month")];
  }
};

export const formatIsoDate = (value?: Dayjs | null) =>
  value ? value.format("YYYY-MM-DD") : undefined;

export const buildProfitLossQuery = (filter?: IAccountManagementProfitLossFilter) => {
  if (!filter) return "";
  const params = new URLSearchParams();
  if (filter.purchaseDateFrom) params.set("purchaseDateFrom", filter.purchaseDateFrom);
  if (filter.purchaseDateTo) params.set("purchaseDateTo", filter.purchaseDateTo);
  if (filter.orderStatus) params.set("orderStatus", filter.orderStatus);
  if (filter.shipmentStatus) params.set("shipmentStatus", filter.shipmentStatus);
  if (filter.country) params.set("country", filter.country);
  if (filter.hasNote != null) params.set("hasNote", String(filter.hasNote));
  if (filter.search) params.set("search", filter.search.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

export const buildListPeriodQuery = (filter?: IAccountManagementCustomerListFilter) => {
  const params = new URLSearchParams();
  if (filter?.search) params.set("search", filter.search);
  if (filter?.periodStart) params.set("periodStart", filter.periodStart);
  if (filter?.periodEnd) params.set("periodEnd", filter.periodEnd);
  const qs = params.toString();
  return qs ? `&${qs}` : "";
};

export const formatPeriodLabel = (from?: string, to?: string) => {
  if (!from && !to) return dayjs().format("MM/YYYY");
  if (from && to) {
    const f = dayjs(from);
    const t = dayjs(to);
    if (f.isSame(t, "month") && f.date() === 1 && t.date() === t.daysInMonth()) {
      return f.format("MM/YYYY");
    }
    return `${f.format("DD/MM/YYYY")} – ${t.format("DD/MM/YYYY")}`;
  }
  return from ? dayjs(from).format("DD/MM/YYYY") : dayjs(to!).format("DD/MM/YYYY");
};

export const matchesPreset = (
  preset: QuickDatePreset,
  from?: string,
  to?: string
): boolean => {
  if (!from || !to) return false;
  const [rangeFrom, rangeTo] = presetToRange(preset);
  return from === formatIsoDate(rangeFrom) && to === formatIsoDate(rangeTo);
};
